import { CombinedGraphQLErrors, type ErrorLike, gql } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { Fragment, useMemo, useRef } from 'react';

import { ClinicianChip, IClinicianChip } from '../../../components/clinicians/ClinicianChip';
import { DateTimeRangeDisplay } from '../../../components/dates/DateTimeRangeDisplay';
import { PatientChipGql, VisitEncounterChips } from '../../../components/displayFields/chips';
import {
    AxleDialogPrimaryButton,
    AxleDialogTitleContent,
} from '../../../components/layout/AxleDialog';
import { VerticalStack } from '../../../components/utils/linearLayouts';
import { VisitDateTypeDisplay } from '../../../components/visits/VisitDateTypeDisplay';
import { ManagedDialog } from '../../../dialogs/ManagedAxleDialog';
import { AxleErrorBoundary } from '../../../fetching/errorBoundaries';
import { AxleSuspense } from '../../../fetching/suspense';
import { NewTabLink } from '../../../utils/links';

export type SchedulingConflict = {
    message: string;
    visitConflictsByClinician: Record<string, string[]>;
};

type SchedulingConflictErrorMetadata = {
    visit_conflicts_by_clinician?: Record<string, string[]>;
};

/**
 * Extracts SchedulingConflictError details from a mutation error, if present.
 * The backend attaches `metadata.visit_conflicts_by_clinician` (a map of
 * clinician ID to conflicting visit IDs) to the error extensions when one or
 * more clinicians has a conflicting visit at the requested time.
 *
 * Apollo Client v4 surfaces GraphQL errors as a `CombinedGraphQLErrors`; network
 * and protocol errors flow through the same `ErrorLike`, so we gate on it.
 */
export const getSchedulingConflict = (error: ErrorLike): SchedulingConflict | null => {
    if (!CombinedGraphQLErrors.is(error)) {
        return null;
    }
    for (const graphQLError of error.errors) {
        const metadata = graphQLError.extensions?.metadata as
            | SchedulingConflictErrorMetadata
            | undefined;
        const visitConflictsByClinician = metadata?.visit_conflicts_by_clinician;
        if (visitConflictsByClinician && Object.keys(visitConflictsByClinician).length > 0) {
            return { message: graphQLError.message, visitConflictsByClinician };
        }
    }
    return null;
};

// Colocated, intentionally not exported. Selection is inlined (no shared
// fragment) per the partner convention, and every conflicting visit is fetched
// in a single bulk request keyed off the IDs in the conflict payload.
const SCHEDULING_CONFLICT_VISITS_QUERY = gql`
    query SchedulingConflictVisits($visitIds: [UUID!]!) {
        visits(filters: { id: { inList: $visitIds } }, ordering: { startDatetime: ASC }) {
            items {
                id
                startDatetime
                durationMinutes
                timezone
                effectiveDate
                patient {
                    id
                    fullName
                }
                clinicianAppointments {
                    id
                    clinician {
                        id
                        firstName
                        lastName
                        logoUrl
                    }
                }
                encounters {
                    id
                    isCompleted
                    service {
                        id
                        name
                    }
                }
            }
        }
    }
`;

type ConflictVisit = {
    id: string;
    // `startDatetime`/`effectiveDate` are read back as `Date` via the cache
    // field policies (FieldPolicies.datetime / .date), not raw strings.
    startDatetime: Date | null;
    durationMinutes: number;
    timezone: string;
    effectiveDate: Date | null;
    patient: { id: string; fullName: string };
    clinicianAppointments: { id: string; clinician: IClinicianChip | null }[] | null;
    encounters: {
        id: string;
        isCompleted: boolean | null;
        service: { id: string; name: string };
    }[];
};

type SchedulingConflictVisitsData = {
    visits: { items: ConflictVisit[] };
};

const ConflictVisitRow = ({ visit }: { visit: ConflictVisit }) => {
    const clinician = visit.clinicianAppointments?.[0]?.clinician ?? null;
    const startMoment = moment(visit.startDatetime);
    const endMoment = startMoment.clone().add(visit.durationMinutes, 'minutes');
    return (
        <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
                {/* Date/time links to the conflicting visit in a new tab. */}
                <NewTabLink to={`/visits/${visit.id}`} style={{ display: 'inline-block' }}>
                    {visit.startDatetime ? (
                        <>
                            <Typography variant={'caption'}>Visit Date/Time</Typography>
                            <DateTimeRangeDisplay
                                startDatetime={startMoment.toDate()}
                                endDatetime={endMoment.toDate()}
                                timezone={visit.timezone}
                            />
                        </>
                    ) : (
                        <>
                            <Typography variant={'caption'}>Visit Date</Typography>
                            <VisitDateTypeDisplay
                                label={false}
                                startDatetime={null}
                                effectiveDate={visit.effectiveDate}
                                tentativeDate={null}
                                fulfillmentWindow={null}
                                fallbackDate={null}
                                timezone={visit.timezone}
                            />
                        </>
                    )}
                </NewTabLink>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Box marginBottom={2}>
                    <Typography variant={'caption'}>Patient</Typography>
                    <Box>
                        <PatientChipGql patient={visit.patient} showLink={true} />
                    </Box>
                </Box>
                <Box>
                    <Typography variant={'caption'}>Services</Typography>
                    <Box>
                        <VisitEncounterChips encounters={visit.encounters} />
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Typography variant={'caption'}>Assignee</Typography>
                <Box>
                    {/* Assignee chip links to the clinician's page in a new tab.
                        ClinicianChip's own link only ever opens same-tab and the
                        chip otherwise renders non-interactive, so the anchor
                        wrapper supplies both the new-tab target and the link
                        hover affordance (pointer + hover background). */}
                    {clinician ? (
                        <Box
                            component={'a'}
                            href={`/clinicians/${clinician.id}`}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            sx={{
                                display: 'inline-block',
                                textDecoration: 'none',
                                // Mimic a clickable chip: pointer cursor, and a
                                // visibly darker fill on hover (the default chip
                                // fill is ~0.08 alpha, so it must go darker than
                                // theme `action.hover` at 0.04 to register).
                                '& .MuiChip-root': { cursor: 'pointer' },
                                '&:hover .MuiChip-root': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.16)',
                                },
                            }}
                        >
                            <ClinicianChip clinician={clinician} showLink={false} />
                        </Box>
                    ) : (
                        <ClinicianChip clinician={null} showLink={false} />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

const ConflictVisitRows = ({ visitIds }: { visitIds: string[] }) => {
    const { data } = useSuspenseQuery<SchedulingConflictVisitsData, { visitIds: string[] }>(
        SCHEDULING_CONFLICT_VISITS_QUERY,
        { variables: { visitIds } },
    );

    return (
        <>
            {data.visits.items.map((visit, index) => (
                <Fragment key={visit.id}>
                    {index > 0 && <Divider />}
                    <ConflictVisitRow visit={visit} />
                </Fragment>
            ))}
        </>
    );
};

const SchedulingConflictDialogContent = ({
    visitIds,
    close,
    onBookAnyway,
}: {
    visitIds: string[];
    close: () => void;
    onBookAnyway: () => void;
}) => (
    <>
        {/* `dividers` + the parent dialog's scroll='paper' keep the title and
            actions pinned while only this content area scrolls. */}
        <DialogContent dividers>
            <VerticalStack spacing={3}>
                <Typography variant={'body1'}>
                    Booking anyway will double-book the assignee(s) below.
                </Typography>
                {/* The rows are the only part that depends on the bulk query, so
                    only they suspend — the title and footer stay visible. */}
                <AxleErrorBoundary>
                    <AxleSuspense skeletonHeight={'120px'} skeletonPadding={2}>
                        <ConflictVisitRows visitIds={visitIds} />
                    </AxleSuspense>
                </AxleErrorBoundary>
            </VerticalStack>
        </DialogContent>
        <DialogActions>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                    width: '100%',
                }}
            >
                <Button variant={'outlined'} fullWidth onClick={close} sx={{ flex: 1 }}>
                    Cancel
                </Button>
                <AxleDialogPrimaryButton
                    label={'Book anyway'}
                    onClick={onBookAnyway}
                    sx={{ flex: 1 }}
                />
            </Box>
        </DialogActions>
    </>
);

/**
 * Confirmation dialog shown when a create-visit mutation fails with a
 * SchedulingConflictError. It bulk-fetches the conflicting visits named in the
 * error payload and lists them (date/time, patient, services, assignee).
 *
 * Opened via `openDialog(DialogType.SchedulingConflict, ...)`:
 *  - "Book anyway" calls `onConfirm` (re-runs the mutation allowing conflicts).
 *  - Closing the dialog any other way calls `onDismiss`, letting each call site
 *    react to an abandoned override (clear the slot, evict cached availability).
 */
export const SchedulingConflictDialog = ({
    conflict,
    onConfirm,
    onDismiss,
    onClose,
}: {
    conflict: SchedulingConflict;
    /** Re-run the create-visit mutation with scheduling conflicts allowed. */
    onConfirm: () => void;
    /** Called when the dialog is closed without confirming the override. */
    onDismiss: () => void;
    /** Injected by the DialogManager to unmount the dialog. */
    onClose: () => void;
}) => {
    const confirmedRef = useRef(false);

    const visitIds = useMemo(
        () => [...new Set(Object.values(conflict.visitConflictsByClinician).flat())],
        [conflict],
    );

    // Confirming resubmits immediately (matching the create-visit flow's prior
    // behavior) so navigation isn't delayed by the close animation. The
    // transition-exited handler only needs to cover the dismiss path.
    const handleExited = () => {
        if (!confirmedRef.current) {
            onDismiss();
        }
        onClose();
    };

    const count = visitIds.length;

    return (
        <ManagedDialog
            dataTestId={'scheduling-conflict-dialog'}
            onClose={handleExited}
            scroll={'paper'}
            maxWidth={'md'}
            fullWidth
        >
            {({ close }) => (
                <>
                    <DialogTitle>
                        <AxleDialogTitleContent
                            title={`${count} conflicting visit${count === 1 ? '' : 's'}`}
                            close={close}
                        />
                    </DialogTitle>
                    <SchedulingConflictDialogContent
                        visitIds={visitIds}
                        close={close}
                        onBookAnyway={() => {
                            confirmedRef.current = true;
                            onConfirm();
                            close();
                        }}
                    />
                </>
            )}
        </ManagedDialog>
    );
};
